import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { filterBy } from '@progress/kendo-data-query';
import {
    DropDownList,
    DropDownListProps,
    DropDownListFilterChangeEvent,
    DropDownListOpenEvent,
    DropDownListCloseEvent,
    DropDownListChangeEvent,
    DropDownListPageChangeEvent,
    DropDownListHandle,
} from '@progress/kendo-react-dropdowns';
import { InputSuffix } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import cn from 'clsx';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import InformationIcon from '@components-clean/core/icons/information-icon';
import ValidationIconSuffix from '@components-clean/core/icons/validation-icon-suffix';
import { TextBox } from '@components-clean/core/text-box';
import Tooltip from '@components-clean/core/tooltip';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';

import './index.scss';

export type ExcludeArray<T> = T extends Array<unknown> ? never : T;

export interface BasicDropDownProps<T> extends Omit<
    DropDownListProps,
    'onFilterChange' | 'popupSettings' | 'virtual' | 'onPageChange'
> {
    'data-testid': string;
    data: ExcludeArray<T>[];
    value?: ExcludeArray<T>;
    hintText?: string;
    suffix?: ReactElement;
    hideBorder?: boolean;
    readOnly?: boolean;
    activeField?: string;
    tooltipText?: string;
    tooltipClassName?: string;
    type?: 'default' | 'card-header';
    cardHeader?: boolean;
    allowClear?: boolean;
    customClassName?: string;
    appendTo?: HTMLDivElement;
    closeWhenThisIsScrolled?: HTMLDivElement | null;
    monitoringToolkitTopbar?: boolean;
    /**
     * Opt-in client-side virtualization for very large local datasets (e.g. tens of
     * thousands of items). When set, only `virtualPageSize` items are rendered into the
     * DOM at a time and more are paged in as the user scrolls, instead of the full `data`
     * array being rendered up front. No-op when omitted - existing callers are unaffected.
     */
    virtualPageSize?: number;
}

const headerItemRender = (li: ReactElement<HTMLLIElement>) => {
    const itemHeader = <span className="headerItem">{li.props.children as ReactNode}</span>;
    return React.cloneElement(li, li.props, itemHeader);
};

const stickyHeaderRender = (div: ReactElement<HTMLDivElement>) => {
    const item = <div className={cn('headerItem sticky')}>{div.props.children as ReactNode}</div>;
    return React.cloneElement(div, div.props, item);
};

const BasicDropDown = <T,>({
    onOpen,
    onClose,
    className,
    tooltipClassName,
    data,
    valid,
    validationMessage,
    label,
    monitoringToolkitTopbar,
    filterable = false,
    hintText,
    'data-testid': testId,
    disabled,
    suffix,
    hideBorder = false,
    readOnly,
    activeField,
    tooltipText,
    type = 'default',
    textField,
    cardHeader,
    valueRender,
    defaultItem,
    value,
    dataItemKey,
    allowClear = false,
    customClassName,
    appendTo,
    itemRender,
    onChange,
    closeWhenThisIsScrolled,
    virtualPageSize,
    ...props
}: BasicDropDownProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [datasource, setDatasource] = useState<T[]>([]);
    const [filter, setFilter] = useState<string>('');
    const [virtualSkip, setVirtualSkip] = useState(0);
    const ref = useRef<DropDownListHandle>(null);
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState<ExcludeArray<T> | undefined>(value);
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const { readOnly: readonlyCtx } = useReadOnlyContext();

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', testId);
    }, []);

    const resetDatasource = () => {
        setDatasource(data ? [...data] : []);
        setVirtualSkip(0);
    };

    useEffect(() => {
        resetDatasource();
    }, [data]);

    const onFilterChange = (event: DropDownListFilterChangeEvent) => {
        setDatasource(filterBy([...data], event.filter));
        setFilter(event.filter.value as string);
        setVirtualSkip(0);
    };

    // Only relevant when virtualPageSize is set - pages through `datasource` (already
    // filtered) instead of handing Kendo the full array to render into the DOM at once.
    const onPageChange = (event: DropDownListPageChangeEvent) => {
        setVirtualSkip(event.page.skip);
    };
    const pagedDatasource = virtualPageSize ? datasource.slice(virtualSkip, virtualSkip + virtualPageSize) : datasource;

    // Add an open handler so that we can add a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onOpenHandler = (event: DropDownListOpenEvent) => {
        setIsOpen(true);

        if (onOpen) {
            onOpen(event);
        }
    };

    // Add a close handler so that we can remove a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onCloseHandler = (event: DropDownListCloseEvent) => {
        setIsOpen(false);
        setFilter('');
        resetDatasource();

        if (onClose) {
            onClose(event);
        }
    };

    const getActiveValue = () => {
        if (inputValue && typeof inputValue === 'object' && activeField && Object.hasOwn(inputValue, activeField)) {
            // @ts-ignore
            return Boolean(inputValue[activeField] as unknown);
        }
        return true;
    };

    const getValueExists = (valueForRender: ExcludeArray<T>) => {
        if (
            valueForRender &&
            typeof valueForRender === 'object' &&
            dataItemKey &&
            Object.hasOwn(valueForRender, dataItemKey)
        ) {
            // @ts-ignore
            return valueForRender[dataItemKey] != null;
        }
        return valueForRender != null;
    };

    useEffect(() => {
        const handleScroll = () => {
            closeWhenThisIsScrolled?.focus();
        };

        closeWhenThisIsScrolled?.addEventListener('scroll', handleScroll, true);

        return () => {
            closeWhenThisIsScrolled?.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    if (readOnly || readonlyCtx) {
        let textValue: string = '';
        let active = true;

        try {
            if (inputValue != null) {
                if (typeof inputValue === 'object') {
                    if (textField && Object.hasOwn(inputValue, textField)) {
                        // @ts-ignore
                        textValue = String(inputValue[textField] as unknown);
                    }
                    active = getActiveValue();
                } else {
                    textValue = inputValue.toString();
                }
            }
        } catch {
            window.console.error('Could not determine text value for readonly drop down list');
        }
        return (
            <TextBox
                data-testid={testId}
                name={props.name}
                value={textValue}
                readOnly
                label={label}
                fillMode={props.fillMode}
                inactiveValue={!active}
                type={type}
                inline={monitoringToolkitTopbar}
                suffix={suffix}
                className={className}
            />
        );
    }

    const classes = cn(className, {
        'opra-combobox-open': isOpen,
        '!k-border-hidden': hideBorder,
        'opra-inactive-value': !getActiveValue(),
        'card-header': type === 'card-header',
        'is-read-only': readOnly,
        'card-header-dropdown': cardHeader,
    });

    const clearValueItem: ExcludeArray<T> =
        ((dataItemKey && textField && allowClear
            ? {
                  [textField]: t('cm2ClearValue'),
                  [dataItemKey]: null,
              }
            : undefined) as ExcludeArray<T>) || (defaultItem as ExcludeArray<T>);

    const noDataRender = (divProps: ReactElement<HTMLDivElement>) => {
        const children = [
            <div key="no-data" className="k-d-flex k-d-flex-row k-justify-content-center k-align-items-center">
                <FontAwesomeIcon data-testid="no-data-found-icon" icon={['far', 'close']} fontSize={20} />
                <p className="k-pl-2 k-my-0">{t('cm2NoResults')}</p>
            </div>,
        ];

        return React.cloneElement(divProps, divProps.props, children);
    };

    const defaultItemRender = (li: ReactElement<HTMLLIElement>) => {
        const childrenItems = [...(li.props.children as Iterable<ReactNode>)];

        return React.cloneElement(li, li.props, childrenItems);
    };

    const handleOnChange = (event: DropDownListChangeEvent) => {
        if (!onChange) return;
        if (event.value === clearValueItem)
            onChange({
                ...event,
                target: {
                    ...event.target,
                    value: null,
                },
                value: null,
            });
        else onChange(event);
    };
    const kendoDropdown = (
        <DropDownList
            ref={ref}
            {...props}
            listNoDataRender={noDataRender}
            textField={textField}
            data={pagedDatasource}
            virtual={
                virtualPageSize ? { pageSize: virtualPageSize, skip: virtualSkip, total: datasource.length } : undefined
            }
            onPageChange={virtualPageSize ? onPageChange : undefined}
            filterable={filterable && !!data.length}
            filter={filter}
            onFilterChange={onFilterChange}
            onOpen={onOpenHandler}
            onClose={onCloseHandler}
            value={disabled ? null : inputValue}
            disabled={disabled || readOnly}
            className={classes}
            popupSettings={{ className: cn({ 'hide-default': !data.length }, customClassName), appendTo }}
            valid={valid}
            itemRender={itemRender || defaultItemRender}
            onChange={handleOnChange}
            valueRender={
                valueRender !== undefined
                    ? valueRender
                    : (element, valueForRender) => {
                          if (!valueForRender) {
                              return element;
                          }

                          element.props.className.concat('k-d-block');

                          const textToRender = getValueExists(valueForRender as ExcludeArray<T>) ? (
                              <span title={tooltipText}>{element.props.children as Iterable<ReactNode>}</span>
                          ) : (
                              <span className="dropdown-placeholder" title={tooltipText}>
                                  {disabled ? '' : t('cm2SelectValue')}
                              </span>
                          );
                          const children = [
                              <Tooltip key="tooltip" position="bottom" parentTitle isInactive={false}>
                                  {textToRender}
                              </Tooltip>,
                              <div key="spacer?" style={{ width: '100%' }} />,
                          ];

                          if (valid === false) {
                              children.push(
                                  <ValidationIconSuffix
                                      key="validation-message"
                                      validationMessage={validationMessage}
                                      padding="none"
                                      maxWidth={320}
                                  />,
                              );
                          }
                          if (!getActiveValue()) {
                              children.push(
                                  <InputSuffix key="inactive-icon">
                                      <InactiveIcon
                                          dataTestId={testId}
                                          tooltipPosition="left"
                                          isSuffix
                                          padding="none"
                                      />
                                  </InputSuffix>,
                              );
                          }
                          if (suffix) {
                              children.push(suffix);
                          }

                          return React.cloneElement(element, { ...element.props, title: undefined }, children);
                      }
            }
            groupStickyHeaderItemRender={stickyHeaderRender}
            groupHeaderItemRender={headerItemRender}
            defaultItem={clearValueItem}
            dataItemKey={dataItemKey}
        />
    );

    const tooltipKendoDropdown =
        disabled && tooltipText ? (
            <Tooltip position="bottom" isInactive={false} tooltipClassName={tooltipClassName}>
                <span title={tooltipText}> {kendoDropdown} </span>
            </Tooltip>
        ) : (
            kendoDropdown
        );

    return (
        <div
            className={cn('basic-drop-down-list k-w-full', {
                'k-flex-grow': !props.style?.width,
                'k-d-flex k-gap-3 k-align-items-center': monitoringToolkitTopbar,
            })}
        >
            <Label
                editorRef={ref}
                className={cn('k-d-flex k-align-items-end', {
                    '!k-pb-0 k-white-space-nowrap': monitoringToolkitTopbar,
                })}
            >
                {label}
                {hintText && <InformationIcon title={hintText} className="k-ml-1.5" dataTestId={`${testId}-hint`} />}
            </Label>
            {tooltipKendoDropdown}
        </div>
    );
};

interface BasicDropDownControllerProps<TData, TControl extends FieldValues> extends BasicDropDownProps<TData> {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean>;
    handleInputChange?: (event: DropDownListChangeEvent) => void;
}

const BasicDropDownController = <TData, TControl extends FieldValues>({
    control,
    name,
    data,
    disabled = false,
    'data-testid': dataTestId,
    label,
    textField,
    dataItemKey,
    required = false,
    validateRules,
    groupField,
    fillMode = 'solid',
    handleInputChange,
    ...props
}: BasicDropDownControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                    validateDefault: (value) => {
                        if (required) {
                            if (dataItemKey && value[dataItemKey] == null) return t('ra:requiredFieldError');
                            if (value == null) return t('ra:requiredFieldError');
                            return true;
                        }
                        return true;
                    },
                },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <BasicDropDown
                    {...props}
                    value={value as ExcludeArray<TData>}
                    onChange={(event) => {
                        onChange(event);
                        props.onChange?.(event);
                        if (handleInputChange) handleInputChange(event);
                    }}
                    data={data}
                    data-testid={dataTestId}
                    label={label}
                    disabled={disabled}
                    textField={textField}
                    dataItemKey={dataItemKey}
                    valid={!error}
                    validationMessage={error?.message}
                    groupField={groupField}
                    fillMode={fillMode}
                    className={`k-h-17/2 k-w-full ${props.className ?? ''}`}
                />
            )}
        />
    );
};

export { BasicDropDown, BasicDropDownController };
